<script setup>
import { mapState } from 'vuex';
import { useStore } from 'vuex';
import { ref, computed, watch, onMounted } from 'vue';
import { useModalProvider } from '@/composables/useModalProvider';
import "@sapudex/web-components/dist/Divider.js";
import PFBaseModal from "@/components/common/modal/PFBaseModal.vue";
import PEBrandNameAddComponent from '@/components/editor/popup/PEBrandNameAddComponent';

const store = useStore();
const showDeleteConfirmModal = ref(false);
const editedBrandNames = ref([]);
const disableAddBrandNameButton = ref(false);
const brandNameProp = ref({ idx: null, brandName: null });
const maxBrandHeaderChar = 56;

const { openModal:openBrandNameModal, isModalOpen:isBrandNameModalOpen } = useModalProvider('brandNameModal');
const { openModal:openAddBrandNameModal, isModalOpen:isAddBrandNameModalOpen, closeModal:closeAddBrandNameModal } = useModalProvider('addBrandNameModal');
const { openModal:openDeleteBrandNameModal, isModalOpen:isDeleteBrandNameModalOpen, closeModal:closeDeleteBrandNameModal } = useModalProvider('deleteBrandNameModal');

onMounted(() => {
  editedBrandNames.value = brandNames.value ? JSON.parse(JSON.stringify(brandNames.value)) : [];
  disableAddBrandNameButton.value = editedBrandNames.value.length >= 10;
  sortByBrandName();
});

const brandNames = computed(() => store.state.partnerProfile.profileData.brandNames);

watch(brandNames, (val) => {
  editedBrandNames.value = val ? JSON.parse(JSON.stringify(val)) : [];
  sortByBrandName();
}, { deep: true });

watch(editedBrandNames, (val) => {
  disableAddBrandNameButton.value = val.length >= 10;
}, { deep: true });

function sortByBrandName() {
  editedBrandNames.value.sort((a, b) => a.brandName.localeCompare(b.brandName));
  let favouriteNameIdx = editedBrandNames.value.findIndex(name => name.isFavoriteName);
  if (favouriteNameIdx > -1) {
    const favouriteNameObj = editedBrandNames.value[favouriteNameIdx];
    editedBrandNames.value.splice(favouriteNameIdx, 1);
    editedBrandNames.value.unshift(favouriteNameObj);
  }
};

function addBrandName(newBrandName) {
  if (newBrandName.isFavoriteName && editedBrandNames.value[0]?.isFavoriteName) {
    editedBrandNames.value[0].isFavoriteName = false;
  }
  editedBrandNames.value.push(newBrandName);
  sortByBrandName();
  closeAddBrandNameModal();
  saveEditedBrandNames();
};

function editBrandName(newBrandName, idx) {
  if (newBrandName.isFavoriteName && editedBrandNames.value[0]?.isFavoriteName) {
    editedBrandNames.value[0].isFavoriteName = false;
  }
  editedBrandNames.value[idx] = newBrandName;
  sortByBrandName();
  closeAddBrandNameModal();
  saveEditedBrandNames();
};

function deleteBrandName(idx) {
  editedBrandNames.value.splice(idx, 1);
  sortByBrandName();
  closeDeleteBrandNameModal();
  saveEditedBrandNames();
};

function saveEditedBrandNames() {
  store.commit('partnerProfile/updateBrandNames', editedBrandNames.value);
};

function updateBrandNameProp(brandName, idx) {
  brandNameProp.value = { idx, brandName };
};

function getTruncatedText(brand) {
  let maxChar = brand.isFavoriteName ? maxBrandHeaderChar - 14 : maxBrandHeaderChar;
  maxChar = maxChar < 5 ? 5 : maxChar;
  
  if (brand.brandName.length <= maxChar) {
    return brand.brandName;
  }
  
  return `${brand.brandName.substring(0, maxChar - 2)}...`;
};
</script>

<template>
  <div class="brand-name-container my-5 ml-3">
    <div class="brand-name-button mt-2" @click="openBrandNameModal(true)">
      <div class="brand-name-more-text-container" v-if="brandNames && brandNames.length > 0">
        <span class="brand-name-more-text">{{brandNames.length}}
          {{ $tc("editor.brand.name.header.more.names", brandNames.length) }}</span>
        <span class="brand-name-text-header-edit ml-2">{{ $t("button.edit") }}</span>
      </div>
      <div v-else class="brand-name-text-header-add">{{ $t("editor.brand.name.header.add") }}</div>
    </div>
    

    <PFBaseModal v-if="isBrandNameModalOpen" :modalHeaderText="$t('editor.brand.name.header')" modalControlKey="brandNameModal">
      <div class="max-h-80">
        <div class="brand-name-text mb-5">
          {{ $t("editor.brand.name.text") }}
        </div>           
        <div v-for="(brandName, idx) in editedBrandNames" :key="brandName.brandName + '-' + idx" class="mt-3">
          <span class="brand-name-text-name mt-2">
            {{getTruncatedText(brandName)}}
          </span>
          <span class="brand-name-favorite" v-show="brandName.isFavoriteName">
            {{ $t('editor.brand.favorite.name.text') }}
          </span>
          <udex-button
            icon="delete" 
            @click="updateBrandNameProp(brandName, idx);openDeleteBrandNameModal()"
            design="Invisible"
            class="ml-2 brand-name-text-header-delete"
            size="Small"
            accessibleName="Delete Brand Name"
          >
            {{ $t("button.delete") }}
          </udex-button>
          <udex-button
            icon="edit" 
            @click="updateBrandNameProp(brandName, idx);openAddBrandNameModal()"
            design="Invisible"
            class="brand-name-text-brand-header-edit"
            size="Small"
            accessibleName="Edit Brand Name"
          >
            {{ $t("button.edit") }}
          </udex-button>
          <div class="brand-name-text-description mt-2 mb-4">
            {{brandName.shortDescription}}
          </div>
          <udex-divider v-if="idx < editedBrandNames.length - 1" class="mt-4"/>
        </div>              

        <div class="flex mt-5">
          <udex-button
            @click="updateBrandNameProp(null, null);openAddBrandNameModal()"
            design="Secondary"
            class="mb-5"
            size="Large"
            slot="endContent"
            :disabled="disableAddBrandNameButton"
            accessibleName="Add Brand Name"
          >
            {{ $t("editor.brand.add.name") }}
          </udex-button>
          <div v-show="disableAddBrandNameButton" class="add-max-brand-name ml-1">
            {{ $t('editor.brand.name.max') }}
          </div>
        </div>
      </div>
    </PFBaseModal>

    <PEBrandNameAddComponent
      v-if="isAddBrandNameModalOpen"
      :brandNameProp="brandNameProp"
      :submitAction="editBrandName"
      :addAction="addBrandName"
      :cancelAction="closeAddBrandNameModal"
    />

    <PFBaseModal v-if="isDeleteBrandNameModalOpen" :modalHeaderText="$t('editor.brand.delete.name')" modalControlKey="deleteBrandNameModal">
      <template #default>
        <div class="brand-name-text">
          {{ $t('editor.brand.delete.name.text') }}
        </div>             
      </template>           

      <template #buttons>
        <udex-button
          @click="deleteBrandName(brandNameProp.idx)"
          design="Negative"
          size="Large"
          slot="endContent"
          accessibleName="Delete Brand Name"
        >
          {{ $t("button.delete") }}
        </udex-button>
        <udex-button
          @click="closeDeleteBrandNameModal"
          design="Secondary"
          size="Large"
          slot="endContent"
          accessibleName="Cancel Deleting Brand Name"
        >
          {{ $t("button.cancel") }}
        </udex-button>
      </template>
    </PFBaseModal>

  </div>
</template>

<style lang="less" scoped>
.brand-name-container {
  display: inline-block;
  width: 100%;
  min-width: 150px;
  cursor: default;
}
.brand-name-more-text-container {
  &:extend(.font-expanded);
  text-align: left;
}
.brand-name-button {
  &:extend(.font-normal);
  max-width: 180px;
  font-size: 16px;
  display: block;
}
.brand-name-favorite {
  &:extend(.font-normal);
  font-size: 16px;
  line-height: 24px;
  color: @color-gray-3;
}
.brand-name-more-text {
  font-size: 16px;
  line-height: 24px;
  color: @color-gray-2;
}
.brand-name-text {
  &:extend(.body-s-size);
  font-style: normal;
}
.brand-name-text-header-edit, .brand-name-text-header-add, .brand-name-more-text {
  &:extend(.font-expanded);
}
.brand-name-text-header-edit, .brand-name-text-header-add {
  font-size: 16px;
  line-height: 24px;
  color: @color-link;
  cursor: pointer;
}
.brand-name-text-header-edit:hover, .brand-name-text-header-add:hover {
  color: @color-link__hover;
}
.brand-name-text-header-delete {
  color: var(--Color-Red-8, #AA0808);
}
.brand-name-text-header-delete:hover {
  color: @color-editor-footer-error;
}
.brand-name-text-brand-header-edit {
  color: #0040B0;
}
.brand-name-text-brand-header-edit:hover {
  color: @color-link__hover;
}
.brand-name-text-brand-header-edit, .brand-name-text-header-delete {
  &:extend(.HeadingMediumXXXXXS-size);
  font-style: normal;
  line-height: 15px;
  display: inline-block;
  float: right;
}
.brand-name-text-name {
  &:extend(.HeadingMediumXXXXS-size);
  color: @udexCoreTextDark;
  font-style: normal;
}
.brand-name-text-description {
  &:extend(.body-xs-size);
  color: @udexCoreTextDark;
  font-style: normal;
}
.add-max-brand-name {
  &:extend(.font-normal);
  font-size: 14px;
  line-height: 40px;
  color: @color-gray-3;
}
</style>
